/**
 * Validate Formatted Numeric - Validators for formatted numeric fields.
 * Provides validators for zip codes, phone numbers, SSN/EIN, medical codes, etc.
 */

// Declare external dependencies

// Types
interface FormattedNumericElement {
  name: string;
  state?: FormattedNumericElement | string | null;
  canadian?: boolean;
  ein?: boolean;
  ssn?: boolean;
  allowExtension?: boolean;
  alpha?: boolean;
  minimum?: any;
  maximum?: any;
  converter?: (value: string) => number | null;
  get: () => string;
  set: (value: string) => void;
  setMessage: (message: string) => boolean;
  getSibling: (name: string) => FormattedNumericElement;
  registerEvent: (handler: string, event: string) => void;
  compareNullable: (value: any, operator: string) => boolean;
}

interface ColorsMap {
  [color: string]: string;
}

/**
 * Phone pad conversion map for letter to number conversion.
 */
const PHONE_PAD = "QZ |   |ABC|DEF|GHI|JKL|MNO|PRS|TUV|WXY";

/**
 * Hex digits for color conversion.
 */
const hexDigits = "0123456789ABCDEF";

/**
 * Color name to hex value map.
 */
const colors: ColorsMap = buildColorList("black000000", "maroon840000", "green008200", "navy000084", "silverC6C3C6", "redFF0000", "lime00FF00", "blue0000FF",
  "gray848284", "purple840084", "olive848200", "teal008284", "whiteFFFFFF", "fuchsiaFF00FF", "yellowFFFF00", "aqua00FFFF");

/**
 * Validate a zip code or postal code field.
 * @this FormattedNumericElement
 * @returns true if valid, false otherwise
 */
function validateZip(this: FormattedNumericElement): boolean {
  const provinces = " AB,BC,MB,NB,NF,NS,NT,ON,PE,QC,SK,YT";
  if (this.state != null && typeof (this.state) == "string") {
    this.state = this.getSibling(this.state);
    this.state.registerEvent("thisObj.validateSibling('" + this.name + "')", "change");
  }

  let isCanadian: boolean;
  const postalCodeStr = parsePostalCode(this.get());
  const zipCodeStr = parseZipCode(postalCodeStr);
  if (this.state != null && (this.state as FormattedNumericElement).get() > "") {
    isCanadian = provinces.indexOf((this.state as FormattedNumericElement).get().toUpperCase()) > 0;
  } else {
    isCanadian = postalCodeStr.length > zipCodeStr.length;
  }
  if (isCanadian) {
    this.set(formatPostalCode(postalCodeStr));
    if (this.canadian == false) return this.setMessage("Canadian postal codes are not allowed");
    return this.setMessage(checkPostalCode(postalCodeStr));
  }
  this.set(formatZipCode(zipCodeStr));
  if (this.canadian == true) return this.setMessage("U.S. zip codes are not allowed");
  return this.setMessage(checkZipCode(zipCodeStr));
}

/**
 * Parse postal code from input string.
 * @param value - Input string
 * @returns Cleaned postal code string
 */
function parsePostalCode(value: string): string {
  return extract(value.toUpperCase(), NUMERIC + UPPERALPHA);
}

/**
 * Check if postal code is valid.
 * @param cleanStr - Cleaned postal code string
 * @returns Error message or empty string
 */
function checkPostalCode(cleanStr: string): string {
  if (cleanStr.length != 6) return "Canadian postal codes are 6 characters long";
  for (let i = 0; i < 6; i++)
    if (((i % 2 == 0) ? UPPERALPHA : NUMERIC).indexOf(cleanStr.charAt(i)) == -1)
      return "Canadian postal codes are in the format Letter/Number/Letter (space) Number/Letter/Number.";
  const notInFirst = "WZ", notInPostalCode = "DFIOQU";
  if (notInFirst.indexOf(cleanStr.charAt(0)) != -1) return "Canadian postal codes may not begin with W or Z.";
  for (let i = 0; i < 6; i += 2)
    if (notInPostalCode.indexOf(cleanStr.charAt(i)) != -1) return "Canadian postal codes may not contain D, F, I, O, Q or U.";
  return "";
}

/**
 * Format postal code with space.
 * @param value - Cleaned postal code string
 * @returns Formatted postal code
 */
function formatPostalCode(value: string): string {
  return (value.length > 3) ? value.substring(0, 3) + " " + value.substring(3) : value;
}

/**
 * Parse zip code from input string.
 * @param value - Input string
 * @returns Cleaned zip code string
 */
function parseZipCode(value: string): string {
  return extract(value, NUMERIC);
}

/**
 * Check if zip code is valid.
 * @param cleanStr - Cleaned zip code string
 * @param fiveDigit - Optional constraint for 5-digit only
 * @returns Error message or empty string
 */
function checkZipCode(cleanStr: string, fiveDigit?: boolean): string {
  if (fiveDigit == true && cleanStr.length != 5)
    return "Zip codes must be exactly 5 digits long.";
  else if (fiveDigit == false && cleanStr.length != 9)
    return "Zip codes must be exactly 9 digits long.";
  else if (cleanStr.length != 5 && cleanStr.length != 9)
    return "Zip codes must be exactly 5 or 9 digits long.";
  return "";
}

/**
 * Format zip code with hyphen.
 * @param value - Cleaned zip code string
 * @returns Formatted zip code
 */
function formatZipCode(value: string): string {
  return (value.length > 5) ? value.substring(0, 5) + '-' + value.substring(5) : value;
}

/**
 * Validate a tax ID (SSN or EIN) field.
 * @this FormattedNumericElement
 * @returns true if valid, false otherwise
 */
function validateTaxId(this: FormattedNumericElement): boolean {
  const cleanStr = extract(this.get(), NUMERIC);
  if (cleanStr.length == 9) {
    let isEIN = (this.ein == true);
    if (this.ssn == true && isEIN) {
      const withHyphens = extract(this.get(), NUMERIC + "-");
      isEIN = withHyphens.length == cleanStr.length + 1 && withHyphens.indexOf("-") == 2;
    }
    if (isEIN)
      this.set(cleanStr.substring(0, 2) + '-' +
        cleanStr.substring(2));
    else {
      const ssn = cleanStr.substring(0, 3) + '-' +
        cleanStr.substring(3, 5) + '-' +
        cleanStr.substring(5, 9);
      this.set(ssn);
      if (cleanStr.substring(0, 3) == "000")
        return this.setMessage("A social security number cannot begin with 000.");
      else if (cleanStr == "111111111" || cleanStr == "333333333" || cleanStr == "999999999" || cleanStr == "123456789")
        return this.setMessage(ssn + " is not a valid social security number.");
    }
    return true;
  }
  let mssg: string;
  if (this.ein == true)
    mssg = (this.ssn == true) ? "Tax identification numbers (SSN or EIN)" : "Employer identification numbers";
  else
    mssg = "Social security numbers";
  return this.setMessage(mssg + " must have exactly 9 digits");
}

/**
 * Validate a Medicare number field.
 * @this FormattedNumericElement
 * @returns true if valid, false otherwise
 */
function validateMedicare(this: FormattedNumericElement): boolean {
  const cleanStr = extract(this.get(), ALPHANUMERIC);
  this.set(cleanStr);
  if (cleanStr.length > 12) {
    return this.setMessage("A medicare number cannot contain more than 12 character.");
  }
  let alphas = 0;
  while (alphas < cleanStr.length && ALPHA.indexOf(cleanStr.charAt(alphas)) != -1) {
    alphas++;
  }
  if (alphas != 0) {
    if (alphas > 3) {
      return this.setMessage("A medicare number may begin with a maximum of 3 alphabetic characters.");
    }
    const after = cleanStr.substring(alphas);
    if (after.length != 6 && after.length != 9 || extract(after, NUMERIC) != after) {
      return this.setMessage("A medicare number beginning with an alphabetic character must have exactly 6 or exactly 9 digits.");
    }
  } else if (extract(cleanStr.substring(0, 9), NUMERIC).length != 9) {
    return this.setMessage("A medicare number beginning with a numeric character must start with at least 9 digits.");
  }
  return true;
}

/**
 * Validate a Medicaid number field.
 * @this FormattedNumericElement
 * @returns true if valid, false otherwise
 */
function validateMedicaid(this: FormattedNumericElement): boolean {
  const cleanStr = extract(this.get(), ALPHANUMERIC);
  this.set(cleanStr);
  if (cleanStr.length > 14) {
    return this.setMessage("A medicaid number cannot contain more than 14 character.");
  }
  return true;
}

/**
 * Validate an ICD-9-CM code field.
 * @this FormattedNumericElement
 * @returns true if valid, false otherwise
 */
function validateICD9CM(this: FormattedNumericElement): boolean {
  let cleanStr = extract(this.get(), ALPHANUMERIC + ".").toUpperCase();
  this.set(cleanStr);
  if (extract(cleanStr.substring(1), NUMERIC + ".") != cleanStr.substring(1)) {
    return this.setMessage("An ICD-9 code cannot have alphabetic characters except as the first character.");
  }
  let pos = cleanStr.indexOf(".");
  if (pos == -1) {
    pos = cleanStr.length;
    cleanStr += ".";
    this.set(cleanStr);
  } else if (pos == 0) {
    return this.setMessage("An ICD-9 code cannot begin with a period.");
  } else if (cleanStr.indexOf(".", pos + 1) != -1) {
    return this.setMessage("An ICD-9 code must have exactly one period.");
  } else if (cleanStr.length > pos + 3) {
    return this.setMessage("An ICD-9 code cannot have more than 2 digits after the period.");
  }
  const c = cleanStr.charAt(0);
  if (c != "E" && c != "V" && (c < "0" || c > "9")) {
    return this.setMessage("An ICD-9 code should begin with an E, V or digit.");
  }
  if (pos != (c == "E" ? 4 : 3)) {
    return this.setMessage("There should be " + (c == "V" ? 2 : 3) + " digits before the period.");
  }
  return true;
}

/**
 * Validate a 16-digit member benefit ID field.
 * @this FormattedNumericElement
 * @returns true if valid, false otherwise
 */
function validate16Digit(this: FormattedNumericElement): boolean {
  let cleanStr = extract(this.get(), NUMERIC);
  for (let i = 12; i >= 4; i -= 4)
    if (cleanStr.length >= i) cleanStr = cleanStr.substring(0, i) + '-' + cleanStr.substring(i);
  this.set(cleanStr);
  if (cleanStr.length == 19) return true;
  return this.setMessage("16-digit MemberBenefit ID's must have exactly 16 digits.");
}

/**
 * Validate a phone number field.
 * @this FormattedNumericElement
 * @returns true if valid, false otherwise
 */
function validatePhone(this: FormattedNumericElement): boolean {
  let cleanStr = cleanPhone(this.get());
  if (this.allowExtension == false)
    cleanStr = cleanStr.substring(0, 10);
  this.set(formatPhone(cleanStr));
  if (cleanStr.length < 10) return this.setMessage("Phone numbers must contain at least 10 digits (including area code).");
  if (cleanStr.length >= 6 && badAreaAndPrefix(cleanStr)) return this.setMessage("Area codes and prefixes cannot begin with 0 or 1");
  return true;
}

/**
 * Clean phone number string.
 * @param strPhone - Input phone string
 * @returns Cleaned phone string
 */
function cleanPhone(strPhone: string): string {
  let i: number, c: string, out = "", cleanStr = extract(strPhone, NUMERIC + ALPHA).toUpperCase();
  if (cleanStr.charAt(0) == '1' && cleanStr.length > 10) cleanStr = cleanStr.substring(1);
  for (i = 0; i < cleanStr.length; i++) {
    c = cleanStr.charAt(i);
    if (NUMERIC.indexOf(c) != -1)
      out += c;
    else if (i < 10)
      out += PHONE_PAD.indexOf(c) >> 2;
  }
  return out;
}

/**
 * Check for bad area code or prefix.
 * @param phoneNum - Phone number string
 * @returns true if area code or prefix is invalid
 */
function badAreaAndPrefix(phoneNum: string): boolean {
  const c0 = phoneNum.charAt(0), c3 = phoneNum.charAt(3);
  return c0 == '0' || c0 == '1' || c3 == '0' || c3 == '1';
}

/**
 * Format phone number with parentheses and hyphen.
 * @param phoneNum - Cleaned phone number string
 * @returns Formatted phone number
 */
function formatPhone(phoneNum: string): string {
  const len = phoneNum.length;
  let out = "";
  if (len > 0) out += "(" + phoneNum.substring(0, 3);
  if (len > 3) out += ") " + phoneNum.substring(3, 6);
  if (len > 6) out += "-" + phoneNum.substring(6, 10);
  if (len > 10) out += "x" + phoneNum.substring(10);
  return out;
}

/**
 * Validate a measurement (height) field.
 * @this FormattedNumericElement
 * @param getFn - If true, returns the compare function
 * @returns true if valid, or compare function if getFn is true
 */
function validateMeasurement(this: FormattedNumericElement, getFn?: boolean): boolean | ((value: any) => number | null) {
  if (getFn) {
    this.converter = getInches;
    return compareConvertedFEW;
  }
  const inches = getInches(this.get());
  if (inches == null) return this.setMessage("This is a required field");
  this.set(formatMeasure(inches, this.alpha));
  const overMax = this.compareNullable(this.maximum, ">");
  if (this.compareNullable(this.minimum, "<"))
    return this.setMessage("Measurement must not be shorter than " + formatMeasure(getValueFor(this.minimum, getInches), this.alpha) + ".");
  if (overMax)
    return this.setMessage("Measurement must not be larger than " + formatMeasure(getValueFor(this.maximum, getInches), this.alpha) + ".");
  return true;
}

/**
 * Convert measurement string to inches.
 * @param str - Input measurement string
 * @returns Total inches or null
 */
function getInches(str: string): number | null {
  let val = extract(str.toLowerCase(), NUMERIC + "i\"f'");
  if (val == "") return null;
  val += "i\"f'";
  const ft_1 = val.indexOf("'"), ft_2 = val.indexOf("f");
  const in_1 = val.indexOf('"'), in_2 = val.indexOf("i");
  const ftEnd = (ft_1 < ft_2) ? ft_1 : ft_2;
  const inEnd = (in_1 < in_2) ? in_1 : in_2;
  const ftStart = (ftEnd < inEnd) ? 0 : inEnd;
  const inStart = (ftEnd < inEnd) ? ftEnd : 0;
  const ftMeasure = parseInt("0" + extract(val.substring(ftStart, ftEnd), NUMERIC), 10);
  const inMeasure = parseInt("0" + extract(val.substring(inStart, inEnd), NUMERIC), 10);
  return ftMeasure * 12 + inMeasure;
}

/**
 * Format inches as feet and inches.
 * @param inches - Total inches
 * @param alpha - Use alphabetic format
 * @returns Formatted measurement string
 */
function formatMeasure(inches: number, alpha?: boolean): string {
  const ftMeasure = parseInt(String(inches / 12));
  const inMeasure = inches % 12;
  return ftMeasure + (alpha ? "ft " : "'") + inMeasure + (alpha ? "in" : '"');
}

/**
 * Build color name to hex value map.
 * @param args - Color definitions in format "namehexvalue"
 * @returns Color map object
 */
function buildColorList(...args: string[]): ColorsMap {
  const numArgs = args.length;
  const out: ColorsMap = {};
  for (let i = 0; i < numArgs; i++) {
    const arg = args[i];
    const len = arg.length - 6;
    out[arg.substring(0, len).toUpperCase()] = arg.substring(len);
  }
  return out;
}

/**
 * Validate a color field.
 * @this FormattedNumericElement
 * @returns true if valid, false otherwise
 */
function validateColor(this: FormattedNumericElement): boolean {
  const colorVal = this.get().toUpperCase();
  if (colors[colorVal] != null) {
    this.set("#" + colors[colorVal]);
    return true;
  }

  const hexVal = extract(colorVal, NUMERIC + "ABCDEF#");
  if (hexVal.length == 7 && hexVal.charAt(0) == "#" && hexVal.indexOf("#", 1) == -1) {
    this.set(hexVal);
    return true;
  }
  if (hexVal.length == 6 && hexVal.indexOf("#") == -1) {
    this.set("#" + hexVal);
    return true;
  }
  return this.setMessage("The color value should be in the form #RRGGBB where RR, GG, and BB are the hexidecimal values of the red, green, and blue parts of the color.");
}

/**
 * Validate a blood pressure field.
 * @this FormattedNumericElement
 * @returns true if valid, false otherwise
 */
function validateBloodPressure(this: FormattedNumericElement): boolean {
  if (/^([3-8][0-9]|9[0-9]|[12][0-9]{2}|3[0-5][0-9]|360)\/([3-8][0-9]|9[0-9]|[12][0-9]{2}|3[0-5][0-9]|360)$/.test(this.get())) {
    return true;
  } else {
    return this.setMessage("Systolic and Diastolic range is between 30 and 360.");
  }
}

/**
 * Convert a number to hexadecimal string.
 * @param val - Number to convert
 * @returns Hexadecimal string
 */
function toHex(val: number): string {
  return (val < 16) ? hexDigits.charAt(val) : (toHex(val >> 4) + hexDigits.charAt(val & 15));
}

// Export for module usage
export {
  validateZip,
  parsePostalCode,
  checkPostalCode,
  formatPostalCode,
  parseZipCode,
  checkZipCode,
  formatZipCode,
  validateTaxId,
  validateMedicare,
  validateMedicaid,
  validateICD9CM,
  validate16Digit,
  validatePhone,
  cleanPhone,
  badAreaAndPrefix,
  formatPhone,
  validateMeasurement,
  getInches,
  formatMeasure,
  buildColorList,
  validateColor,
  validateBloodPressure,
  toHex,
  PHONE_PAD,
  hexDigits,
  colors
};

// Export types
export type { FormattedNumericElement, ColorsMap };

// Expose on globalThis for backwards compatibility
(globalThis as any).validateZip = validateZip;
(globalThis as any).parsePostalCode = parsePostalCode;
(globalThis as any).checkPostalCode = checkPostalCode;
(globalThis as any).formatPostalCode = formatPostalCode;
(globalThis as any).parseZipCode = parseZipCode;
(globalThis as any).checkZipCode = checkZipCode;
(globalThis as any).formatZipCode = formatZipCode;
(globalThis as any).validateTaxId = validateTaxId;
(globalThis as any).validateMedicare = validateMedicare;
(globalThis as any).validateMedicaid = validateMedicaid;
(globalThis as any).validateICD9CM = validateICD9CM;
(globalThis as any).validate16Digit = validate16Digit;
(globalThis as any).validatePhone = validatePhone;
(globalThis as any).cleanPhone = cleanPhone;
(globalThis as any).badAreaAndPrefix = badAreaAndPrefix;
(globalThis as any).formatPhone = formatPhone;
(globalThis as any).validateMeasurement = validateMeasurement;
(globalThis as any).getInches = getInches;
(globalThis as any).formatMeasure = formatMeasure;
(globalThis as any).buildColorList = buildColorList;
(globalThis as any).validateColor = validateColor;
(globalThis as any).validateBloodPressure = validateBloodPressure;
(globalThis as any).toHex = toHex;
(globalThis as any).PHONE_PAD = PHONE_PAD;
(globalThis as any).hexDigits = hexDigits;
(globalThis as any).colors = colors;

// Script resource notification
if (typeof _SR_ !== "undefined" && _SR_ !== null) {
  _SR_.notify("validateformattednumeric.js");
}
